import { useCallback, useEffect, useRef, useState } from 'react';
import { NavLink } from 'react-router-dom';
import { useAuth } from '@/lib/auth/useAuth';
import { endpoints } from '@/lib/api/endpoints';
import styles from './Nav.module.css';

/**
 * Nav bar. Visual rules live in `Nav.module.css` rather than a global
 * stylesheet — a global stylesheet regressed every page +2-7pp due to
 * cascade conflicts with Tailwind, so do not re-add one.
 *
 * The MARS menu is driven by the backend: `/auth/status` returns
 * `portfolio_funds` (the active funds this user may access, from the same
 * `funds_for` gate the API enforces), plus `is_fulcrum_internal` for the
 * MARS Live link. `FUND_NAV_LABELS` below is presentation only — it maps
 * fund IDs to short menu labels and fixes the display order; an unknown
 * fund ID still gets a link (labelled by its ID) so a newly-granted fund
 * never silently disappears from the menu.
 * With no MARS access at all the menu collapses to Central Bank Tracker
 * only. The Logout link is always rendered at the far right.
 */

interface NavItemDef {
  readonly to: string;
  readonly label: string;
}

const MARS_LIVE: NavItemDef = { to: '/mars-live', label: 'MARS Live' };

const FUND_NAV_LABELS: ReadonlyArray<{ fund: string; label: string }> = [
  { fund: 'mars-dar-nn', label: 'MARS in DAR NN' },
  { fund: 'mars-fqm', label: 'MARS in FQM' },
  { fund: 'mars-fidelity-balanced', label: 'Balanced' },
  { fund: 'mars-fidelity-conservative', label: 'Conservative' },
];

function fundNavItem(fund: string, label: string): NavItemDef {
  return { to: `/mars-portfolio-dashboard?fund=${fund}`, label };
}

function marsMenuFor(
  isFulcrumInternal: boolean,
  portfolioFunds: readonly string[],
): readonly NavItemDef[] | null {
  const items: NavItemDef[] = isFulcrumInternal ? [MARS_LIVE] : [];
  for (const { fund, label } of FUND_NAV_LABELS) {
    if (portfolioFunds.includes(fund)) items.push(fundNavItem(fund, label));
  }
  for (const fund of portfolioFunds) {
    if (!FUND_NAV_LABELS.some((entry) => entry.fund === fund)) {
      items.push(fundNavItem(fund, fund));
    }
  }
  return items.length > 0 ? items : null;
}

function CentralBankLink({ onClick }: { onClick: () => void }): JSX.Element {
  return (
    <NavLink to="/central-bank" className={styles.linkWithBadge} onClick={onClick}>
      Central Bank Tracker<sup className={styles.betaBadge}>Beta</sup>
    </NavLink>
  );
}

export default function Nav(): JSX.Element {
  const { isFulcrumInternal, portfolioFunds } = useAuth();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [marsOpen, setMarsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const closeMobile = useCallback(() => {
    setMobileOpen(false);
    setMarsOpen(false);
  }, []);

  const toggleMobile = useCallback(() => {
    setMobileOpen((v) => !v);
  }, []);

  const toggleMars = useCallback((e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    setMarsOpen((v) => !v);
  }, []);

  // Close dropdown on outside click / Escape.
  useEffect(() => {
    if (!marsOpen) return;
    function onDocClick(e: MouseEvent): void {
      if (!dropdownRef.current) return;
      if (!dropdownRef.current.contains(e.target as Node)) setMarsOpen(false);
    }
    function onKey(e: KeyboardEvent): void {
      if (e.key === 'Escape') setMarsOpen(false);
    }
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [marsOpen]);

  const marsItems = marsMenuFor(isFulcrumInternal, portfolioFunds);
  const navLinksClass = mobileOpen ? `${styles.navLinks} ${styles.navLinksOpen}` : styles.navLinks;
  const dropdownClass = marsOpen ? `${styles.dropdown} ${styles.dropdownActive}` : styles.dropdown;

  return (
    <nav className={styles.navbar}>
      <div className={styles.navContainer}>
        {/* Logo always returns to Overview (`/`). Uses NavLink for SPA
         * routing so the click is instant — a plain `<a href>` would
         * trigger a full page reload. */}
        <NavLink to="/" className={styles.logo} onClick={closeMobile}>
          <img
            src="/static/logos/image.png"
            alt="Central Bank Communication"
            className={styles.logoImg}
          />
        </NavLink>

        <button
          type="button"
          className={`${styles.navToggle} ${mobileOpen ? styles.navToggleActive : ''}`}
          aria-label="Toggle navigation"
          aria-expanded={mobileOpen}
          onClick={toggleMobile}
        >
          <span></span>
          <span></span>
          <span></span>
        </button>

        <div className={navLinksClass}>
          {marsItems ? (
            <>
              <NavLink to="/" end onClick={closeMobile}>Overview</NavLink>
              <NavLink to="/economic-outlook" onClick={closeMobile}>Economic Outlook</NavLink>
              <CentralBankLink onClick={closeMobile} />
              <NavLink to="/forecasts" onClick={closeMobile}>Forecasts</NavLink>
              <div className={dropdownClass} ref={dropdownRef}>
                <a
                  href="#"
                  className={styles.dropdownToggle}
                  onClick={toggleMars}
                  aria-expanded={marsOpen}
                  aria-haspopup="menu"
                >
                  MARS Portfolio <span className={styles.dropdownArrow}>▼</span>
                </a>
                <div className={styles.dropdownMenu} role="menu">
                  {marsItems.map((item) => (
                    <NavLink
                      key={item.to}
                      to={item.to}
                      role="menuitem"
                      onClick={closeMobile}
                    >
                      {item.label}
                    </NavLink>
                  ))}
                </div>
              </div>
            </>
          ) : (
            /* Fallback: Central Bank Tracker only. */
            <CentralBankLink onClick={closeMobile} />
          )}

          {/* Logout is always rendered. Flask `/logout` clears the session. */}
          <a href={endpoints.auth.logout()}>Logout</a>
        </div>
      </div>
    </nav>
  );
}
